import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, ShieldCheck, Smartphone, Monitor, LogOut, AlertTriangle,
  Copy, CheckCircle2, X,
} from "lucide-react";

/**
 * SecuritySettings — patient/provider self-service page for:
 *   - Enabling / disabling MFA (TOTP)
 *   - Reviewing active sessions (signed-in devices)
 *   - Revoking individual sessions or all others
 *
 * All actions are audit-logged and mirrored to the immutable audit chain.
 */
export default function SecuritySettings() {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [setup, setSetup] = useState(null); // { qr_data_url, secret, backup_codes }
  const [verifyCode, setVerifyCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [showDisable, setShowDisable] = useState(false);

  const load = async () => {
    try {
      const [meRes, sessRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/auth/sessions"),
      ]);
      setMfaEnabled(!!meRes.data.mfa_enabled);
      setSessions(sessRes.data.sessions || []);
    } catch (e) {
      toast.error("Failed to load security info");
    }
  };
  useEffect(() => { load(); }, []);

  async function startMfaSetup() {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "MFA setup failed");
    } finally {
      setBusy(false);
    }
  }

  async function verifyMfa() {
    if (!verifyCode || verifyCode.length < 6) return;
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify", { code: verifyCode });
      toast.success("Multi-factor authentication enabled");
      setSetup(null); setVerifyCode(""); setMfaEnabled(true);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code — try again");
    } finally {
      setBusy(false);
    }
  }

  async function disableMfa() {
    if (!disableCode) return;
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: disableCode });
      toast.success("Multi-factor authentication disabled");
      setShowDisable(false); setDisableCode(""); setMfaEnabled(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  }

  async function revokeSession(id) {
    if (!window.confirm("Sign out this device? The user will be immediately logged out there.")) return;
    try {
      await api.post(`/auth/sessions/${id}/revoke`);
      toast.success("Device signed out");
      await load();
    } catch { toast.error("Revoke failed"); }
  }

  async function revokeAllOthers() {
    if (!window.confirm("Sign out of every other device? You'll stay signed in here.")) return;
    try {
      const { data } = await api.post("/auth/sessions/revoke_all_others");
      toast.success(`Signed out of ${data.revoked} device(s)`);
      await load();
    } catch { toast.error("Revoke failed"); }
  }

  const copyToClipboard = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch { /* no-op */ }
  };

  return (
    <div className="space-y-6" data-testid="security-settings-page">
      {/* MFA */}
      <div className="ros-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
               style={{ background: mfaEnabled ? "#4ADE8018" : "#F8717118",
                        border: `1px solid ${mfaEnabled ? "#4ADE8040" : "#F8717140"}` }}>
            {mfaEnabled ? <ShieldCheck size={22} className="text-[#4ADE80]" />
                        : <Shield size={22} className="text-[#F87171]" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-heading text-lg text-white">Two-factor authentication</h3>
              {mfaEnabled ? (
                <span className="text-[10px] font-bold text-[#4ADE80] px-2 py-0.5 rounded-full bg-[#4ADE80]/10">ENABLED</span>
              ) : (
                <span className="text-[10px] font-bold text-[#F87171] px-2 py-0.5 rounded-full bg-[#F87171]/10">OFF</span>
              )}
            </div>
            <p className="text-sm text-[#A3ACA7]">
              Requires a 6-digit code from your authenticator app (Google Authenticator, Authy, 1Password) at every sign-in.
              This is the single biggest protection against stolen passwords.
            </p>
            <div className="mt-4 flex gap-2">
              {!mfaEnabled && !setup && (
                <button onClick={startMfaSetup} disabled={busy}
                        data-testid="mfa-enable-btn"
                        className="text-sm bg-[#4ADE80] hover:bg-[#22C55E] text-black font-semibold rounded-lg px-4 py-2 disabled:opacity-50">
                  Enable two-factor
                </button>
              )}
              {mfaEnabled && !showDisable && (
                <button onClick={() => setShowDisable(true)}
                        data-testid="mfa-disable-btn"
                        className="text-sm text-[#F87171] hover:underline">
                  Disable two-factor
                </button>
              )}
            </div>
          </div>
        </div>

        {setup && (
          <MfaSetupPanel
            setup={setup} verifyCode={verifyCode} setVerifyCode={setVerifyCode}
            onVerify={verifyMfa} onCancel={() => setSetup(null)} busy={busy}
            onCopy={copyToClipboard}
          />
        )}

        {showDisable && (
          <div className="mt-6 pt-6 border-t border-[#272E2B]">
            <div className="text-sm text-white mb-3 flex items-center gap-2">
              <AlertTriangle size={14} className="text-[#FBBF24]" />
              Enter your current 6-digit code to confirm.
            </div>
            <div className="flex gap-2">
              <input type="text" inputMode="numeric" maxLength={8}
                     value={disableCode} onChange={e => setDisableCode(e.target.value.replace(/\s/g, ""))}
                     placeholder="123 456"
                     data-testid="mfa-disable-code-input"
                     className="bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm text-white font-mono tracking-wider outline-none focus:border-[#F87171]" />
              <button onClick={disableMfa} disabled={busy || !disableCode}
                      data-testid="mfa-disable-confirm-btn"
                      className="text-sm bg-[#F87171] hover:bg-[#DC2626] text-white rounded-lg px-4 py-2 disabled:opacity-50">
                Disable
              </button>
              <button onClick={() => { setShowDisable(false); setDisableCode(""); }}
                      className="text-sm text-[#A3ACA7] px-3 py-2">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Privacy consents */}
      <div className="ros-card" data-testid="consents-panel">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-[#60A5FA]/10 border border-[#60A5FA]/30 flex items-center justify-center shrink-0">
            <Shield size={22} className="text-[#60A5FA]" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg text-white">Privacy choices</h3>
            <p className="text-sm text-[#A3ACA7] mt-1">
              What we can and can't do with your data. Change your mind anytime — every decision is timestamped and logged.
            </p>
          </div>
        </div>
        <ConsentsList />
      </div>

      {/* Active Sessions */}
      <div className="ros-card" data-testid="sessions-panel">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading text-lg text-white">Active sessions</h3>
            <p className="text-sm text-[#A3ACA7] mt-1">
              Every device where you're currently signed in. Sign out of any you don't recognize.
            </p>
          </div>
          {sessions.length > 1 && (
            <button onClick={revokeAllOthers}
                    data-testid="revoke-all-others-btn"
                    className="text-xs text-[#F87171] hover:underline flex items-center gap-1">
              <LogOut size={12} /> Sign out of all others
            </button>
          )}
        </div>

        <ul className="divide-y divide-[#272E2B]">
          {sessions.map(s => {
            const isMobile = /iOS|Android/.test(s.device_label || "");
            const Icon = isMobile ? Smartphone : Monitor;
            return (
              <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`session-row-${s.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[#1C201E] flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-[#A3ACA7]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white font-medium">{s.device_label}</span>
                    {s.is_current && (
                      <span className="text-[9px] font-bold text-[#4ADE80] px-1.5 py-0.5 rounded-full bg-[#4ADE80]/10">
                        THIS DEVICE
                      </span>
                    )}
                    {s.mfa_verified && (
                      <span className="text-[9px] font-bold text-[#60A5FA] px-1.5 py-0.5 rounded-full bg-[#60A5FA]/10">MFA</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#7A8480]">
                    Last active {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : "—"}
                    {s.ip && <> · {s.ip}</>}
                  </div>
                </div>
                {!s.is_current && (
                  <button onClick={() => revokeSession(s.id)}
                          data-testid={`session-revoke-${s.id}`}
                          className="text-xs text-[#F87171] hover:underline">
                    Sign out
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function ConsentsList() {
  const [latest, setLatest] = React.useState({});
  const [catalog, setCatalog] = React.useState([]);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    const [me, cat] = await Promise.all([
      api.get("/consents/mine"),
      api.get("/consents/catalog"),
    ]);
    setLatest(me.data.latest || {});
    setCatalog(cat.data.consents || []);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  async function toggle(consent_type, currentlyGranted) {
    setBusy(true);
    try {
      const body = currentlyGranted
        ? { revokes: [consent_type], grants: [] }
        : { grants: [consent_type], revokes: [] };
      await api.post("/consents/decide", body);
      toast.success(currentlyGranted ? "Consent revoked" : "Consent granted");
      await load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  }

  return (
    <ul className="divide-y divide-[#272E2B]">
      {catalog.map(c => {
        const cur = latest[c.type];
        const granted = cur?.status === "granted" && cur?.version === c.version;
        return (
          <li key={c.type} className="py-3 flex items-start gap-3" data-testid={`consent-item-${c.type}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-white">{c.label}</span>
                {c.required && (
                  <span className="text-[9px] font-bold text-[#F87171] px-1.5 py-0.5 rounded-full bg-[#F87171]/10">REQUIRED</span>
                )}
                {granted && (
                  <span className="text-[9px] font-bold text-[#4ADE80] px-1.5 py-0.5 rounded-full bg-[#4ADE80]/10">GRANTED</span>
                )}
              </div>
              <div className="text-xs text-[#A3ACA7] mt-1 leading-relaxed">{c.description}</div>
              {cur?.decided_at && (
                <div className="text-[11px] text-[#7A8480] mt-1">
                  {granted ? "Granted" : "Revoked"} {new Date(cur.decided_at).toLocaleString()}
                  {cur.ip && <> · {cur.ip}</>}
                </div>
              )}
            </div>
            <button
              onClick={() => toggle(c.type, granted)}
              disabled={busy}
              data-testid={`consent-toggle-${c.type}`}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                granted
                  ? "border-[#F87171]/30 text-[#F87171] hover:bg-[#F87171]/10"
                  : "border-[#4ADE80]/40 text-[#4ADE80] hover:bg-[#4ADE80]/10"
              }`}
            >
              {granted ? "Revoke" : "Grant"}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function MfaSetupPanel({ setup, verifyCode, setVerifyCode, onVerify, onCancel, busy, onCopy }) {
  return (
    <div className="mt-6 pt-6 border-t border-[#272E2B]" data-testid="mfa-setup-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="ros-label mb-3">Step 1 — scan QR</div>
          <div className="bg-white rounded-lg p-3 inline-block">
            <img src={setup.qr_data_url} alt="MFA QR code" className="w-40 h-40" />
          </div>
          <div className="text-xs text-[#A3ACA7] mt-3">
            Or type this secret into your authenticator manually:
          </div>
          <div className="flex items-center gap-2 mt-2">
            <code className="text-xs font-mono bg-[#1C201E] rounded px-2 py-1 flex-1 truncate text-[#4ADE80]"
                  data-testid="mfa-secret">
              {setup.secret}
            </code>
            <button onClick={() => onCopy(setup.secret)}
                    className="text-[#A3ACA7] hover:text-white p-1">
              <Copy size={14} />
            </button>
          </div>
        </div>

        <div>
          <div className="ros-label mb-3">Step 2 — save backup codes</div>
          <div className="bg-[#1C201E] rounded-lg p-3 mb-3">
            <div className="grid grid-cols-2 gap-1 font-mono text-xs text-[#D1D5DB]">
              {setup.backup_codes.map((c, i) => (
                <div key={i}>{c}</div>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-[#FBBF24] flex items-start gap-1.5">
            <AlertTriangle size={12} className="shrink-0 mt-0.5" />
            <span>Store these somewhere safe. Each code works once if you lose your phone.</span>
          </div>
          <button onClick={() => onCopy(setup.backup_codes.join("\n"))}
                  className="mt-2 text-xs text-[#4ADE80] hover:underline flex items-center gap-1">
            <Copy size={12} /> Copy all backup codes
          </button>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-[#272E2B]">
        <div className="ros-label mb-3">Step 3 — enter a 6-digit code to activate</div>
        <div className="flex gap-2">
          <input type="text" inputMode="numeric" maxLength={8}
                 value={verifyCode}
                 onChange={e => setVerifyCode(e.target.value.replace(/\s/g, ""))}
                 placeholder="123 456"
                 autoFocus
                 data-testid="mfa-verify-code-input"
                 className="bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm text-white font-mono tracking-widest outline-none focus:border-[#4ADE80]" />
          <button onClick={onVerify} disabled={busy || verifyCode.length < 6}
                  data-testid="mfa-verify-btn"
                  className="text-sm bg-[#4ADE80] hover:bg-[#22C55E] text-black font-semibold rounded-lg px-4 py-2 disabled:opacity-50 flex items-center gap-1.5">
            <CheckCircle2 size={14} /> Activate
          </button>
          <button onClick={onCancel}
                  className="text-sm text-[#A3ACA7] px-3 py-2 flex items-center gap-1.5">
            <X size={14} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
